import type { Me } from '@p2b/contract';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router';
import { api, authClient, P2bError } from './api.js';
import { useI18n } from './i18n.js';
import { CliLogin } from './pages/CliLogin.js';
import { Confirm } from './pages/Confirm.js';
import { Consent } from './pages/Consent.js';
import { Home } from './pages/Home.js';
import { Invite } from './pages/Invite.js';
import { Keys } from './pages/Keys.js';
import { Landing } from './landing/Landing.js';
import { AdminUsers } from './pages/AdminUsers.js';
import { Login } from './pages/Login.js';
import { OrgPage } from './pages/Org.js';
import { OrgsPage } from './pages/Orgs.js';
import { PageShell } from './pages/PageShell.js';
import { ProjectPage } from './pages/Project.js';
import { GridBackdrop, Lockup } from './ui.js';

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false, staleTime: 60_000 });
}

export function LangToggle({ className = 'quiet' }: { className?: string }) {
  const { lang, setLang, t } = useI18n();
  return (
    <button
      className={className}
      lang={lang === 'is' ? 'en' : 'is'}
      onClick={() => setLang(lang === 'is' ? 'en' : 'is')}
    >
      {t('language')}
    </button>
  );
}

function initials(name: string, email: string): string {
  const words = (name || email.split('@')[0] || '?').split(/[\s._-]+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/** Name, keys, language and sign-out behind the initials. */
function UserMenu({ me }: { me: Me }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const ref = useRef<HTMLDetailsElement>(null);
  const loc = useLocation();
  useEffect(() => {
    if (ref.current) ref.current.open = false;
  }, [loc.pathname]);
  useEffect(() => {
    const close = (e: MouseEvent | KeyboardEvent) => {
      const el = ref.current;
      if (!el?.open) return;
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !el.contains(e.target as Node)) el.open = false;
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', close);
    };
  }, []);
  const signOut = async () => {
    await authClient.signOut();
    qc.clear();
    window.location.href = '/login';
  };
  return (
    <details className="usermenu" ref={ref}>
      <summary aria-label={me.user.email} title={me.user.email}>
        <span className="avatar">{initials(me.user.name, me.user.email)}</span>
      </summary>
      <div className="menu">
        <div className="menu-id">
          {me.user.name && <strong>{me.user.name}</strong>}
          <span>{me.user.email}</span>
        </div>
        {me.user.isSuperadmin && <Link to="/admin/users">{t('pendingUsers')}</Link>}
        {!me.pending && <Link to="/settings/keys">{t('apiKeys')}</Link>}
        <LangToggle className="menu-item" />
        <button className="menu-item" onClick={signOut}>
          {t('signOut')}
        </button>
      </div>
    </details>
  );
}

/**
 * Verkefni | Fyrirtæki (owner 2026-11-01). Someone in exactly one organization sees its name and goes straight to
 * it; superadmins and people in several get the list.
 */
function MainNav({ me }: { me: Me }) {
  const { t } = useI18n();
  const loc = useLocation();
  const one = !me.user.isSuperadmin && me.orgs.length === 1 ? me.orgs[0]! : null;
  const projectsActive = loc.pathname === '/' || loc.pathname.startsWith('/projects/');
  return (
    <nav className="mainnav" aria-label={t('appName')}>
      <NavLink to="/" className={projectsActive ? 'active' : undefined} end>
        {t('projects')}
      </NavLink>
      {(me.user.isSuperadmin || me.orgs.length > 0) && (
        <NavLink to={one ? `/orgs/${one.slug}` : '/orgs'}>{one ? one.name : t('organizations')}</NavLink>
      )}
    </nav>
  );
}

/** Pages that need a signed-in user; others redirect to /login?next=… */
function Shell({ children }: { children: ReactNode }) {
  const me = useMe();
  const { t } = useI18n();
  const loc = useLocation();
  if (me.isPending) return <div className="center muted">{t('loading')}</div>;
  if (me.error) {
    if (me.error instanceof P2bError && me.error.status === 401) {
      return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
    }
    return (
      <div className="center error">
        {t('error')}: {me.error.message}
      </div>
    );
  }
  const waiting = me.data.pendingUsers ?? 0;
  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-in">
          <Link to="/" className="brand">
            <Lockup />
          </Link>
          {!me.data.pending && <MainNav me={me.data} />}
          <nav className="topnav">
            {me.data.user.isSuperadmin && waiting > 0 && (
              <Link to="/admin/users" className="waiting-link">
                {t('pendingUsers')}
                <span className="count">{waiting}</span>
              </Link>
            )}
            <UserMenu me={me.data} />
          </nav>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}

/** Pages that work signed out (login, confirm, consent, invitation, CLI): one card on the structural grid. */
export function Bare({ children }: { children: ReactNode }) {
  return (
    <div className="bare">
      <GridBackdrop />
      <main className="bare-main">
        <div className="bare-card">
          <h1 className="bare-logo">
            <Link to="/" className="brand">
              <Lockup large />
            </Link>
          </h1>
          {children}
        </div>
        <LangToggle className="quiet bare-lang" />
      </main>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Bare>
            <Login />
          </Bare>
        }
      />
      <Route
        path="/login/confirm"
        element={
          <Bare>
            <Confirm />
          </Bare>
        }
      />
      <Route
        path="/consent"
        element={
          <Bare>
            <Consent />
          </Bare>
        }
      />
      <Route
        path="/invite/:id"
        element={
          <Bare>
            <Invite />
          </Bare>
        }
      />
      <Route
        path="/cli-login"
        element={
          <Bare>
            <CliLogin />
          </Bare>
        }
      />
      <Route path="/" element={<Root />} />
      <Route
        path="/orgs"
        element={
          <Shell>
            <OrgsPage />
          </Shell>
        }
      />
      <Route
        path="/orgs/:org/*"
        element={
          <Shell>
            <OrgPage />
          </Shell>
        }
      />
      <Route
        path="/projects/:org/:slug/*"
        element={
          <Shell>
            <ProjectPage />
          </Shell>
        }
      />
      <Route path="/r/:org/:project/:pub/*" element={<PageShell />} />
      <Route
        path="/admin/users"
        element={
          <Shell>
            <AdminUsers />
          </Shell>
        }
      />
      <Route
        path="/settings/keys"
        element={
          <Shell>
            <Keys />
          </Shell>
        }
      />
      <Route
        path="*"
        element={
          <Shell>
            <NotFound />
          </Shell>
        }
      />
    </Routes>
  );
}

/** "/": the landing page for signed-out visitors, the projects for everyone signed in. */
function Root() {
  const me = useMe();
  if (me.isPending) return <div className="boot" />;
  if (me.error instanceof P2bError && me.error.status === 401) return <Landing />;
  return (
    <Shell>
      <Home />
    </Shell>
  );
}

function NotFound() {
  const { t } = useI18n();
  return (
    <div className="page">
      <h1 className="page-title">{t('notFound')}</h1>
      <Link to="/">{t('back')}</Link>
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const { t } = useI18n();
  if (!error) return null;
  if (error instanceof P2bError) {
    if (error.status === 403)
      return (
        <p className="error" role="alert">
          {t('forbidden')}
        </p>
      );
    if (error.status === 404)
      return (
        <p className="error" role="alert">
          {t('notFound')}
        </p>
      );
    return (
      <p className="error" role="alert">
        {error.message}
      </p>
    );
  }
  return (
    <p className="error" role="alert">
      {(error as Error).message ?? String(error)}
    </p>
  );
}
